import { useEffect, useState, type FormEvent } from "react";
import { supabase, functionErrorMessage } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { Button, Field, Input, Select } from "../../lib/ui.js";
import { SectionCard, errorStyle, linkButtonStyle, mutedStyle, tdStyle, thStyle } from "./common.js";
import { formatDate } from "../../lib/datetime.js";
import { formatAuPhoneForDisplay } from "../../lib/phone.js";

type Role = "owner" | "manager" | "staff" | "viewer";

interface MemberRow {
  user_id: string;
  email: string | null;
  phone: string | null;
  role: Role;
  invited_at: string;
  /** The staff member a staff login is linked to. */
  staff_name: string | null;
}

const ROLE_LABEL: Record<Role, string> = { owner: "Owner", manager: "Manager", staff: "Staff", viewer: "Viewer" };

/**
 * Team member permissions. A member's role is their permission level:
 *   Owner: everything, including adding and removing team members, changing roles, and invoices.
 *   Manager: day-to-day work for the whole business (bookings, calls, customers, services) but not the receptionist rules, business settings, the team or invoices.
 *   Viewer: the manager's day-to-day pages, read-only (migration 0076).
 *   Staff: one staff member's own bookings, working hours and time off. Staff logins are created from the Services & staff page,
 *     where each is linked to the person it belongs to.
 * Reads via rpc_list_business_members; role changes and removals are owner-only RPCs; adding a member goes
 * through the invite-member Edge Function (needs the service-role key to create a new account).
 */
export function TeamSection() {
  const { activeBusinessId, memberships, session } = useAuth();
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [by, setBy] = useState<"email" | "mobile">("email");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState<"owner" | "manager" | "viewer">("manager");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isOwner = memberships.some((m) => m.business_id === activeBusinessId && m.role === "owner");

  async function load() {
    if (!activeBusinessId) return;
    const { data, error: err } = await supabase.rpc("rpc_list_business_members", { p_business_id: activeBusinessId });
    if (!err) setRows((data ?? []) as MemberRow[]);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    setNotice(null);
    setSaving(true);
    const { data, error: err } = await supabase.functions.invoke("invite-member", { body: { business_id: activeBusinessId, [by]: contact, role } });
    setSaving(false);
    if (err) {
      setError(await functionErrorMessage(err));
      return;
    }
    // An existing account is added straight away: no email or text goes out, so say so rather than leave the owner waiting for one.
    if ((data as { already_existed?: boolean } | null)?.already_existed) {
      setNotice(`${contact} already has an account, so no ${by === "email" ? "email" : "text"} was sent. They can sign in as usual and will see this business.`);
    }
    setContact("");
    await load();
  }

  async function handleRoleChange(userId: string, next: Role) {
    if (!activeBusinessId) return;
    setError(null);
    const { error: err } = await supabase.rpc("rpc_set_business_member_role", { p_business_id: activeBusinessId, p_user_id: userId, p_role: next });
    if (err) {
      setError(err.message.includes("last_owner") ? "There must always be at least one owner." : err.message);
    }
    await load();
  }

  async function handleRemove(userId: string) {
    if (!activeBusinessId) return;
    setError(null);
    const { error: err } = await supabase.rpc("rpc_remove_business_member", { p_business_id: activeBusinessId, p_user_id: userId });
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  }

  return (
    <SectionCard
      title="Team member permissions"
      description={
        <>
          <strong>Owner</strong> can do everything, including adding and removing team members, changing these permissions and seeing invoices.{" "}
          <strong>Manager</strong> can run the day-to-day for the whole business (bookings, calls, customers and services) but can't change the receptionist rules or business settings, manage
          the team or see invoices. <strong>Staff</strong> sees and manages only their own bookings and working hours; add a staff login from the
          Services &amp; staff page. <strong>Viewer</strong> sees what a manager sees on the day-to-day pages but can't change anything.
          {!isOwner && " Only an owner can change permissions."}
        </>
      }
    >
      {isOwner && (
        <form onSubmit={handleAdd} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Field label="Invite by">
            <Select value={by} onChange={(e) => setBy(e.target.value as "email" | "mobile")} style={{ width: 110 }}>
              <option value="email">Email</option>
              <option value="mobile">Mobile</option>
            </Select>
          </Field>
          <Field label={by === "email" ? "Email to invite" : "Mobile number to invite"}>
            <Input
              required
              type={by === "email" ? "email" : "tel"}
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              style={{ width: 240 }}
            />
          </Field>
          <Field label="Permission">
            <Select value={role} onChange={(e) => setRole(e.target.value as "owner" | "manager" | "viewer")} style={{ width: 130 }}>
              <option value="manager">Manager</option>
              <option value="viewer">Viewer (read-only)</option>
              <option value="owner">Owner</option>
            </Select>
          </Field>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Inviting..." : "Invite"}
          </Button>
        </form>
      )}
      {isOwner && by === "mobile" && (
        <p style={{ ...mutedStyle, margin: "-6px 0 12px" }}>A mobile login has no password: they sign in with a code we text to this number.</p>
      )}
      {error && <p style={{ ...errorStyle, marginBottom: 10 }}>{error}</p>}
      {notice && <p style={{ ...mutedStyle, marginBottom: 10 }}>{notice}</p>}

      <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle}>Login</th>
            <th style={thStyle}>Permission</th>
            <th style={thStyle}>Since</th>
            {isOwner && <th style={thStyle} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isSelf = r.user_id === session?.user.id;
            return (
              <tr key={r.user_id}>
                <td style={tdStyle}>
                  {r.email ?? (r.phone ? formatAuPhoneForDisplay(r.phone.startsWith("+") ? r.phone : `+${r.phone}`) : "-")}
                  {isSelf && <span style={{ ...mutedStyle, marginLeft: 6 }}>(you)</span>}
                  {r.staff_name && <span style={{ ...mutedStyle, marginLeft: 6 }}>({r.staff_name})</span>}
                </td>
                <td style={tdStyle}>
                  {isOwner ? (
                    <Select value={r.role} onChange={(e) => void handleRoleChange(r.user_id, e.target.value as Role)} style={{ width: 120 }}>
                      <option value="manager">Manager</option>
                      <option value="viewer">Viewer</option>
                      <option value="owner">Owner</option>
                      {r.role === "staff" && <option value="staff">Staff</option>}
                    </Select>
                  ) : (
                    ROLE_LABEL[r.role]
                  )}
                </td>
                <td style={tdStyle}>{formatDate(r.invited_at)}</td>
                {isOwner && (
                  <td style={tdStyle}>
                    {!isSelf && (
                      <button onClick={() => void handleRemove(r.user_id)} style={{ ...linkButtonStyle, color: "var(--rc-danger)" }}>
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={isOwner ? 4 : 3} style={{ ...tdStyle, ...mutedStyle }}>
                No team members yet.
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
    </SectionCard>
  );
}
